import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EnterpriseSidebar } from "./EnterpriseDashboard";
import { useCyclePicker, CyclePickerBar } from "../hooks/useCyclePicker";

const VERDICT_COLORS = {
    Retain:   "#34D399",
    Retrain:  "#FBBF24",
    Redeploy: "#F87171",
};

export default function BoardReport() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const picker = useCyclePicker();
    const { cycleId } = picker;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cycleId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/v2/dashboard/board-report/${cycleId}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { console.error(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [api, cycleId]);

    const handleDownload = async () => {
        if (!cycleId) return;
        try {
            const res = await api.get(`/v2/dashboard/board-report/${cycleId}/pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `realloc_board_report_${data?.cycle?.name?.replace(/\s+/g, "_") || cycleId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
        }
    };

    const s = data?.executive_summary || {};
    const verdicts = data?.verdicts || {};
    const segments = data?.capability_segments || {};
    const bands = data?.displacement_bands || {};
    const flags = data?.advisory_flags || {};
    const regions = data?.region_rollup || {};
    const depts = data?.department_rollup || {};

    return (
        <div className="flex print:block">
            <div className="print:hidden">
                <EnterpriseSidebar current="/enterprise/report" />
            </div>
            <main className="ml-56 print:ml-0 flex-1 p-8 min-h-screen" data-testid="board-report">
                <div className="max-w-5xl space-y-6">
                    <div className="flex items-center justify-between print:hidden">
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white">Board Report</h1>
                            <p className="text-sm text-gray-500 mt-1 font-body">Board-ready summary for the selected cycle</p>
                        </div>
                        <button onClick={handleDownload} disabled={!cycleId}
                            className="text-xs border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 px-4 py-2 disabled:opacity-40"
                            data-testid="download-pdf-btn">
                            Download PDF
                        </button>
                    </div>

                    <div className="print:hidden">
                        <CyclePickerBar picker={picker} />
                    </div>

                    {loading && <p className="text-xs text-gray-500">Loading…</p>}
                    {!cycleId && (
                        <div className="bg-card border border-border/40 p-8 text-center text-gray-500 text-sm print:hidden">
                            Pick a cycle to generate the board report.
                        </div>
                    )}

                    {data && (
                        <>
                            {/* Cover */}
                            <div className="bg-card border border-border/40 p-8" data-testid="board-cover">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Realloc · Workforce Reallocation Infrastructure</p>
                                <h2 className="text-3xl font-display font-bold text-white mt-2">{data.enterprise?.name}</h2>
                                <p className="text-sm text-gray-400 mt-1 font-body">
                                    {data.cycle?.name} · {data.cycle?.status} · methodology {data.cycle?.methodology_version}
                                </p>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                                    {[
                                        { l: "Total Assessed",   v: s.total_assessed ?? 0 },
                                        { l: "Analysed",         v: s.total_analysed ?? 0 },
                                        { l: "Avg Capability",   v: (s.avg_capability ?? 0).toFixed(1) },
                                        { l: "Avg Displacement", v: (s.avg_displacement ?? 0).toFixed(1) },
                                    ].map((k) => (
                                        <div key={k.l} className="border border-border/40 p-4">
                                            <p className="text-xs text-gray-500 uppercase tracking-wider">{k.l}</p>
                                            <p className="text-2xl font-mono font-bold text-white mt-1">{k.v}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Executive summary narrative */}
                            <div className="bg-card border border-border/40 p-6" data-testid="exec-summary">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Executive Summary</h3>
                                <p className="text-sm text-gray-300 font-body leading-relaxed">{s.narrative}</p>
                                <div className="flex items-center gap-6 mt-4 text-xs font-mono text-gray-400">
                                    <span>Strategic Pathway: <span className="text-white">{s.strategic_pathway}</span></span>
                                    <span>Core Builder Rate: <span className="text-white">{s.core_builder_rate}%</span></span>
                                </div>
                            </div>

                            {/* Verdict distribution */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div className="bg-card border border-border/40 p-6" data-testid="board-verdicts">
                                    <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Verdict Distribution</h3>
                                    <div className="space-y-2">
                                        {["Retain", "Retrain", "Redeploy"].map((v) => {
                                            const n = verdicts[v] || 0;
                                            const pct = s.total_analysed ? Math.round(100 * n / s.total_analysed) : 0;
                                            return (
                                                <div key={v}>
                                                    <div className="flex items-center justify-between text-xs mb-1">
                                                        <span style={{ color: VERDICT_COLORS[v] }}>{v}</span>
                                                        <span className="font-mono text-white">{n} ({pct}%)</span>
                                                    </div>
                                                    <div className="h-2 bg-black border border-border/40">
                                                        <div className="h-full" style={{ width: `${pct}%`, background: VERDICT_COLORS[v] }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="bg-card border border-border/40 p-6" data-testid="board-segments">
                                    <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Capability Segments</h3>
                                    <div className="space-y-1.5">
                                        {["Core Builder", "Emerging Builder", "Support Developer", "Restructuring Candidate"].map((seg) => (
                                            <div key={seg} className="flex items-center justify-between text-xs border border-border/40 px-3 py-2">
                                                <span className="text-gray-400">{seg}</span>
                                                <span className="font-mono text-white">{segments[seg] || 0}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Displacement bands */}
                            <div className="bg-card border border-border/40 p-6" data-testid="board-bands">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Displacement Bands</h3>
                                <div className="grid grid-cols-5 gap-2">
                                    {["Low", "Moderate", "Significant Exposure", "High Exposure", "Transforming"].map((b) => (
                                        <div key={b} className="border border-border/40 p-3">
                                            <p className="text-xs text-gray-500">{b}</p>
                                            <p className="text-xl font-mono font-bold text-white mt-1">{bands[b] || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Region + Department rollups */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <RollupTable title="Region Rollup" rows={regions} testId="board-region-rollup" />
                                <RollupTable title="Department Rollup" rows={depts} testId="board-dept-rollup" />
                            </div>

                            {/* Top Builders / Top Risks */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div className="bg-card border border-border/40 p-6" data-testid="board-top-builders">
                                    <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Top Builders</h3>
                                    <div className="space-y-1.5">
                                        {(data.top_builders || []).map((b) => (
                                            <button key={b.candidate_id}
                                                onClick={() => navigate(`/enterprise/worker/${b.candidate_id}`)}
                                                className="w-full flex items-center justify-between text-xs border border-border/40 px-3 py-2 hover:border-white/20 hover:bg-white/5 text-left">
                                                <span className="text-white">{b.full_name}<span className="text-gray-500 ml-2">{b.job_title}</span></span>
                                                <span className="font-mono text-emerald-300">{(b.capability_composite ?? 0).toFixed(1)}</span>
                                            </button>
                                        ))}
                                        {(!data.top_builders || data.top_builders.length === 0) && <p className="text-xs text-gray-600">No builders yet.</p>}
                                    </div>
                                </div>
                                <div className="bg-card border border-border/40 p-6" data-testid="board-top-risks">
                                    <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Top Redeployment Risks</h3>
                                    <div className="space-y-1.5">
                                        {(data.top_risks || []).map((r) => (
                                            <button key={r.candidate_id}
                                                onClick={() => navigate(`/enterprise/worker/${r.candidate_id}`)}
                                                className="w-full flex items-center justify-between text-xs border border-border/40 px-3 py-2 hover:border-white/20 hover:bg-white/5 text-left">
                                                <span className="text-white">{r.full_name}<span className="text-gray-500 ml-2">{r.job_title}</span></span>
                                                <span className="font-mono text-red-300">{(r.displacement_composite ?? 0).toFixed(1)}</span>
                                            </button>
                                        ))}
                                        {(!data.top_risks || data.top_risks.length === 0) && <p className="text-xs text-gray-600">No risks identified.</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Flag rollup */}
                            <div className="bg-card border border-border/40 p-6" data-testid="board-flags">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Advisory Flags Triggered</h3>
                                <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                                    {["SVP", "BI", "CC", "STP", "HOS+", "MVO-L", "RoR"].map((code) => {
                                        const n = flags[code] || 0;
                                        return (
                                            <div key={code} className={`p-3 border ${n > 0 ? "border-amber-500/40 bg-amber-500/5" : "border-border/40 bg-black/30"}`}>
                                                <p className={`text-xs font-mono ${n > 0 ? "text-amber-300" : "text-gray-500"}`}>{code}</p>
                                                <p className={`text-lg font-mono font-bold ${n > 0 ? "text-amber-300" : "text-gray-600"}`}>{n}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <p className="text-xs text-gray-600 text-center mt-6">
                                Generated by Realloc · Workforce Reallocation Infrastructure · {new Date().toLocaleDateString()}
                            </p>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

function RollupTable({ title, rows, testId }) {
    const entries = Object.entries(rows || {});
    return (
        <div className="bg-card border border-border/40 p-6" data-testid={testId}>
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">{title}</h3>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                        <th className="text-left py-1.5">Segment</th>
                        <th className="text-right py-1.5">Total</th>
                        <th className="text-right py-1.5 text-emerald-400">Retain</th>
                        <th className="text-right py-1.5 text-amber-400">Retrain</th>
                        <th className="text-right py-1.5 text-red-400">Redeploy</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(([k, v]) => (
                        <tr key={k} className="border-b border-border/20">
                            <td className="py-1.5 text-white">{k}</td>
                            <td className="py-1.5 text-right font-mono text-gray-300">{v.total || 0}</td>
                            <td className="py-1.5 text-right font-mono text-emerald-300">{v.Retain || 0}</td>
                            <td className="py-1.5 text-right font-mono text-amber-300">{v.Retrain || 0}</td>
                            <td className="py-1.5 text-right font-mono text-red-300">{v.Redeploy || 0}</td>
                        </tr>
                    ))}
                    {entries.length === 0 && (
                        <tr><td colSpan="5" className="py-3 text-center text-gray-600">No data.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
